import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import loader from "../../../../../components/recharts/loader.gif";
import SimulationCumulatedVolumeAnalysis from "./TableBuySell/SimulationCumulatedVolume";
import SimulationCumulatedCostAnalysis from "./TableBuySell/SimulationCumulatedCost";
import BuySellAnalysis from "./BuySellChart/BuySell";
import ToggleTabs from "../../utility/ChartTableToggle";
import { SearchBox } from "../../../../../components/Search";
import {
  ViewSelector,
  VIEW_OPTIONS,
  DELTA_VIEW_OPTIONS,
  DataTabs,
} from "./BuySellChart/BuySell";

export default function SurplusDeficitAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  activeTab,
  selectedExchanges,
  selectedMarkets,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  activeTab: string;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
}) {
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [loading, setLoading] = useState(false);
  const [activeChartTab, setActiveChartTab] = useState<string>("Buy/Sell");

  // Shared Data for Chart -> Table sync
  const [sharedVolumeData, setSharedVolumeData] = useState<any>(null);
  const [sharedDeltaData, setSharedDeltaData] = useState<any>(null);

  // Filter state lifted from BuySell
  const [view, setView] = useState<"buyers" | "sellers">("buyers");
  const [deltaView, setDeltaView] = useState<"drawal" | "injection">("drawal");
  const [selectedEntities, setSelectedEntities] = useState<
    { value: string; label: string }[]
  >([]);
  const [deltaSelectedEntities, setDeltaSelectedEntities] = useState<
    { value: string; label: string }[]
  >([]);
  const [deltaEntityOptions, setDeltaEntityOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedFilterDate, setSelectedFilterDate] = useState<any[]>([]);
  const [deltaSelectedDates, setDeltaSelectedDates] = useState<any[]>([]);
  const [availableDates, setAvailableDates] = useState<any[]>([]);
  const [entityOptions, setEntityOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const dateKey = "Power Market";
    const trendDates = DefaultDates?.[dateKey]?.defaultTrendDates || [];

    setAvailableDates(trendDates);

    if (trendDates.length === 0) {
      if (selectedFilterDate.length > 0) {
        setSelectedFilterDate([]);
      }
      if (deltaSelectedDates.length > 0) {
        setDeltaSelectedDates([]);
      }
      return;
    }

    const primaryDate = trendDates[0];
    const currentSelection = selectedFilterDate[0];

    if (
      !currentSelection ||
      (primaryDate && primaryDate.value !== currentSelection.value)
    ) {
      if (primaryDate) {
        setSelectedFilterDate([primaryDate]);
      } else {
        setSelectedFilterDate([]);
      }
    }

    if (trendDates.length >= 2) {
      setDeltaSelectedDates([trendDates[0], trendDates[1]]);
    } else if (trendDates.length > 0) {
      setDeltaSelectedDates(trendDates);
    }
  }, [DefaultDates]);

  const handleTxViewChange = (newView: string) => {
    setView(newView as "buyers" | "sellers");
  };

  const handleDeltaViewChange = (newView: string) => {
    setDeltaView(newView as "drawal" | "injection");
  };

  const chartTitle = useMemo(() => {
    const blockText =
      selectedBlock && selectedBlock.length > 0
        ? ` (${selectedBlock.join(",")} Time Block)`
        : "";

    if (activeChartTab === "Buy/Sell") {
      const baseText = `Top 10 ${view === "buyers" ? "Buyers" : "Sellers"}`;
      return (
        <>
          {baseText}
          {blockText && <span className="text-[#F1935C]"> in{blockText}</span>}
        </>
      );
    } else {
      return (
        <>
          Top 10 States with Largest{" "}
          {deltaView === "drawal" ? "Drawal" : "Injection"} Changes
          {blockText && <span className="text-[#F1935C]"> in{blockText}</span>}
        </>
      );
    }
  }, [activeChartTab, view, deltaView, selectedBlock]);

  // Shared filter header rendered by both chart and table wrappers
  const filterHeader = (
    <div className="flex justify-between items-center mb-2 border-[#DFEAF2] bg-[#FFF] flex-shrink-0 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        <h2 className="chartHeading !text-[#34656D] !text-sm">{chartTitle}</h2>
      </div>
      <div className="flex gap-x-5 justify-end flex-shrink-0 items-center">
        {activeChartTab === "Buy/Sell" && (
          <>
            <div className="min-w-[180px]">
              <SearchBox
                options={entityOptions}
                selectedOptions={selectedEntities}
                onChange={(val: any) => setSelectedEntities(val ?? [])}
                placeholder="Search entity..."
                isMany={true}
              />
            </div>
            <SearchBox
              options={availableDates}
              isMany={false}
              placeholder="Select Date"
              selectedOptions={selectedFilterDate}
              onChange={(val: any) => {
                if (val) {
                  setSelectedFilterDate(Array.isArray(val) ? val : [val]);
                } else {
                  setSelectedFilterDate([]);
                }
              }}
            />
            <ViewSelector
              selected={view}
              onSelectionChange={handleTxViewChange}
              options={VIEW_OPTIONS}
            />
          </>
        )}
        {activeChartTab === "Buy/Sell Delta" && (
          <>
            <div className="min-w-[180px]">
              <SearchBox
                options={deltaEntityOptions}
                selectedOptions={deltaSelectedEntities}
                onChange={(val: any) => setDeltaSelectedEntities(val ?? [])}
                placeholder="Search entity..."
                isMany={true}
              />
            </div>
            <div className="min-w-[250px]">
              <SearchBox
                options={availableDates}
                isMany={true}
                placeholder="Select Dates"
                selectedOptions={deltaSelectedDates}
                onChange={(val: any) => {
                  if (val && val.length > 0) {
                    setDeltaSelectedDates(Array.isArray(val) ? val : [val]);
                  } else {
                    setDeltaSelectedDates([]);
                  }
                }}
              />
            </div>
            <ViewSelector
              selected={deltaView}
              onSelectionChange={handleDeltaViewChange}
              options={DELTA_VIEW_OPTIONS}
            />
          </>
        )}
        <ToggleTabs
          tabs={DataTabs}
          activeTab={activeChartTab}
          setActiveTab={setActiveChartTab}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full h-full grid grid-rows-[auto_1fr] font-inter">
      {/* Shared Filter Header spanning full width */}
      <div className="w-full bg-white rounded-t-2xl border border-[#DFEAF2] border-b-0 px-2 pt-2">
        {filterHeader}
      </div>

      {/* Chart + Table Row */}
      <div className="w-full min-h-0 flex flex-col lg:flex-row gap-2">
        {/* Chart - 70% width on large screens */}
        <div className="h-full min-w-0 w-full lg:w-[69%] flex-shrink-0">
          {loading ? (
            <div className="w-full h-full bg-white flex items-center justify-center rounded-b-2xl border border-[#E5E7EB]">
              <img src={loader} width={200} alt="loader" className="block" />
            </div>
          ) : (
            <div className="w-full h-full overflow-auto">
              <BuySellAnalysis
                selectedBlock={selectedBlock}
                refreshKey={refreshKey}
                setError={setError}
                selectedExchanges={selectedExchanges}
                selectedMarkets={selectedMarkets}
                activeChartTab={activeChartTab}
                setSharedData={setSharedVolumeData}
                setSharedDeltaData={setSharedDeltaData}
                view={view}
                deltaView={deltaView}
                selectedEntities={selectedEntities}
                deltaSelectedEntities={deltaSelectedEntities}
                selectedFilterDate={selectedFilterDate}
                deltaSelectedDates={deltaSelectedDates}
                onEntityOptionsChange={setEntityOptions}
                onDeltaEntityOptionsChange={setDeltaEntityOptions}
              />
            </div>
          )}
        </div>

        {/* Table Section - 30% width on large screens */}
        <div className="h-full min-w-0 overflow-auto w-full lg:w-[30%] flex-shrink-0">
          {loading ? (
            <div className="w-full h-full bg-white flex items-center justify-center rounded-2xl border border-[#E5E7EB]">
              <img src={loader} width={200} alt="loader" className="block" />
            </div>
          ) : (
            <div className="w-full h-full min-w-0 overflow-hidden">
              {activeTab === "Volume" && (
                <SimulationCumulatedVolumeAnalysis
                  selectedBlock={selectedBlock}
                  refreshKey={refreshKey}
                  setError={setError}
                  setUniqueTargetDate={setUniqueTargetDate}
                  activeChartTab={activeChartTab}
                  sharedData={sharedVolumeData}
                  sharedView={view}
                  sharedDeltaData={sharedDeltaData}
                  sharedDeltaView={deltaView}
                  selectedExchanges={selectedExchanges}
                  selectedMarkets={selectedMarkets}
                  selectedEntities={selectedEntities}
                />
              )}
              {activeTab === "Cost" && (
                <SimulationCumulatedCostAnalysis
                  selectedBlock={selectedBlock}
                  refreshKey={refreshKey}
                  setError={setError}
                  setUniqueTargetDate={setUniqueTargetDate}
                  activeChartTab={activeChartTab}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
